import { css, keyframes } from '@emotion/react';
import React, { useMemo } from 'react';
import {
  lightBlue,
  lightOrange,
  primary,
  purple,
  success,
  teal,
  yellow,
} from 'styles/global_defaults/colors';
import { confettiZIndex } from 'styles/global_defaults/scaffolding';
import { Circle, Star, Triangle } from './particles';

const confettiStyles = css`
  .particle-container {
    top: 50%;
    left: 50%;
    z-index: ${confettiZIndex};
    pointer-events: none;
    .particle {
      top: 0;
      left: 0;
      opacity: 0;
      > * {
        transform: translate(-50%, -50%);
      }
    }
  }
`;

export interface NvConfettiProps {
  numberOfStars?: number;
  numberOfCircles?: number;
  numberOfTriangles?: number;
  starColor?: string;
  circleColor?: string;
  triangleColor?: string;
  duration?: number; // in milliseconds
  particleSize?: {
    width: number;
    height: number;
  };
  children: React.ReactNode;
}

enum ShapeType {
  STAR = 'star',
  CIRCLE = 'circle',
  TRIANGLE = 'triangle',
}

interface ConfettiParticle {
  id: string;
  type: ShapeType;
  color: string;
  angle: number;
  distance: number;
  startOffsetX: number;
  startOffsetY: number;
  delay: number;
  animation: ReturnType<typeof keyframes>;
}

const CONFETTI_COLORS = [
  success,
  teal,
  lightOrange,
  lightBlue,
  purple,
  yellow,
  primary,
];

// Helper to create keyframes animation for a particle
const createAnimation = (
  angle: number,
  distance: number,
  startOffsetX: number,
  startOffsetY: number,
) => {
  const radians = (angle * Math.PI) / 180;
  const endX = Math.cos(radians) * distance;
  const endY = Math.sin(radians) * distance;

  return keyframes`
    0% {
      opacity: 0;
      transform: translate(${startOffsetX}px, ${startOffsetY}px) scale(0.5) rotate(0deg);
    }
    10% {
      opacity: 1;
      transform: translate(${startOffsetX + endX * 0.1}px, ${startOffsetY + endY * 0.1}px) scale(1) rotate(36deg);
    }
    85% {
      opacity: 1;
      transform: translate(${startOffsetX + endX * 0.85}px, ${startOffsetY + endY * 0.85}px) scale(0.8) rotate(306deg);
    }
    100% {
      opacity: 0;
      transform: translate(${startOffsetX + endX}px, ${startOffsetY + endY}px) scale(0.5) rotate(360deg);
    }
  `;
};

const getRandomColor = () => CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];

const NvConfetti = ({
  numberOfStars = 5,
  numberOfCircles = 5,
  numberOfTriangles = 5,
  starColor: starColorProp,
  circleColor: circleColorProp,
  triangleColor: triangleColorProp,
  duration = 2000,
  particleSize = { width: 20, height: 20 },
  children,
}: NvConfettiProps) => {
  const particles = useMemo(() => {
    // Clamp counts to >= 0 to prevent negative values
    const starCount = Math.max(0, numberOfStars);
    const circleCount = Math.max(0, numberOfCircles);
    const triangleCount = Math.max(0, numberOfTriangles);
    const totalParticles = starCount + circleCount + triangleCount;

    // Early return if no particles to avoid Infinity/NaN angles
    if (totalParticles <= 0) {
      return [];
    }

    const result: ConfettiParticle[] = [];
    const angleStep = 360 / totalParticles;

    // Use provided colors or fall back to random colors
    const starColor = starColorProp ?? getRandomColor();
    const circleColor = circleColorProp ?? getRandomColor();
    const triangleColor = triangleColorProp ?? getRandomColor();

    // Helper to calculate starting offset along the radial direction
    const getRadialOffset = (angle: number) => {
      const radians = (angle * Math.PI) / 180;
      const offsetDistance = 20 + Math.random() * 30; // Start 20-50px from center along the radial direction
      return {
        x: Math.cos(radians) * offsetDistance,
        y: Math.sin(radians) * offsetDistance,
      };
    };

    // Create arrays of particle types to distribute
    const particleTypes: Array<{ type: ShapeType; color: string }> = [];
    for (let i = 0; i < starCount; i += 1) {
      particleTypes.push({ type: ShapeType.STAR, color: starColor });
    }
    for (let i = 0; i < circleCount; i += 1) {
      particleTypes.push({ type: ShapeType.CIRCLE, color: circleColor });
    }
    for (let i = 0; i < triangleCount; i += 1) {
      particleTypes.push({ type: ShapeType.TRIANGLE, color: triangleColor });
    }

    // Shuffle the particle types for even distribution
    for (let i = particleTypes.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [particleTypes[i], particleTypes[j]] = [particleTypes[j], particleTypes[i]];
    }

    // Create particles with shuffled types
    particleTypes.forEach((particleType, index) => {
      const angle = angleStep * index + (Math.random() * 20 - 10);
      const radialOffset = getRadialOffset(angle);
      const distance = 80 + Math.random() * 60;
      result.push({
        id: `${particleType.type}-${index}`,
        type: particleType.type,
        color: particleType.color,
        angle,
        distance,
        startOffsetX: radialOffset.x,
        startOffsetY: radialOffset.y,
        delay: Math.random() * 0.3,
        animation: createAnimation(angle, distance, radialOffset.x, radialOffset.y),
      });
    });

    return result;
  }, [
    numberOfStars,
    numberOfCircles,
    numberOfTriangles,
    starColorProp,
    circleColorProp,
    triangleColorProp,
  ]);

  const renderShape = (particle: ConfettiParticle) => {
    switch (particle.type) {
      case ShapeType.STAR:
        return <Star color={particle.color} size={particleSize} />;
      case ShapeType.CIRCLE:
        return <Circle color={particle.color} size={particleSize} />;
      case ShapeType.TRIANGLE:
        return <Triangle color={particle.color} size={particleSize} />;
      default:
        return null;
    }
  };

  return (
    <div
      className='position-relative d-inline-block'
      css={confettiStyles}
    >
      <div
        className='position-absolute particle-container'
        aria-hidden='true'
      >
        {particles.map((particle) => (
          <div
            key={particle.id}
            className='particle position-absolute'
            css={css`
              animation: ${particle.animation} ${duration}ms linear forwards;
              animation-delay: ${particle.delay}s;
            `}
          >
            {renderShape(particle)}
          </div>
        ))}
      </div>
      {children}
    </div>
  );
};

export default NvConfetti;
